import { useEffect, useCallback, useRef } from "react";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useReAuthStore } from "@/stores/auth/reauth-store";
import {
  authDebug,
  authDebugWarn,
  describeAuthError,
} from "@/stores/auth/auth-diagnostics";
import { logger } from "@/utils";

interface UseAuthSessionOptions {
  autoRefresh?: boolean;
  refreshThreshold?: number; // Minutes before expiration to refresh
  onSessionExpired?: () => void;
  onSessionRefreshed?: () => void;
  onSessionError?: (error: Error) => void;
}

export const useAuthSession = (options: UseAuthSessionOptions = {}) => {
  const {
    autoRefresh = true,
    refreshThreshold = 7.5, // 7.5 minutes before expiration (half of 15-min default token)
    onSessionExpired,
    onSessionRefreshed,
    onSessionError,
  } = options;

  const { user, tokens, isAuthenticated, refreshToken } = useAuthStore();
  const { openModal: openReAuthModal } = useReAuthStore();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  const logRefreshFailure = useCallback(
    (context: string, error: unknown) => {
      const details = describeAuthError(error);
      logger.error(context, {
        message: details.message,
      });
    },
    [],
  );

  // Stable refs for callback options (prevents dependency churn)
  const onSessionExpiredRef = useRef(onSessionExpired);
  const onSessionRefreshedRef = useRef(onSessionRefreshed);
  const onSessionErrorRef = useRef(onSessionError);
  onSessionExpiredRef.current = onSessionExpired;
  onSessionRefreshedRef.current = onSessionRefreshed;
  onSessionErrorRef.current = onSessionError;

  /**
   * Check if token is about to expire
   */
  const isTokenNearExpiration = useCallback((): boolean => {
    if (!tokens) return false;

    const now = Date.now();
    const expirationTime = tokens.expiresAt;
    const thresholdTime = refreshThreshold * 60 * 1000; // Convert to milliseconds

    return expirationTime - now <= thresholdTime;
  }, [tokens, refreshThreshold]);

  /**
   * Check if token is expired
   */
  const isTokenExpired = useCallback((): boolean => {
    if (!tokens) return true;
    return Date.now() >= tokens.expiresAt;
  }, [tokens]);

  /**
   * Refresh token if needed
   */
  const refreshIfNeeded = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !tokens || isRefreshingRef.current) {
      return;
    }

    if (isTokenExpired()) {
      try {
        isRefreshingRef.current = true;
        logger.info("Token has expired, attempting refresh before re-auth");
        authDebug("session.expired-refresh.start");

        await refreshToken();
        onSessionRefreshedRef.current?.();
        authDebug("session.expired-refresh.success");

        logger.info("Expired token refreshed successfully");
      } catch (error) {
        authDebugWarn(
          "session.expired-refresh.failed",
          describeAuthError(error),
        );
        logRefreshFailure("Failed to refresh expired token", error);
        onSessionErrorRef.current?.(
          error instanceof Error ? error : new Error("Token refresh failed"),
        );

        openReAuthModal('session_expired', user?.email || undefined);
        onSessionExpiredRef.current?.();
      } finally {
        isRefreshingRef.current = false;
      }
      return;
    }

    if (isTokenNearExpiration() && autoRefresh) {
      try {
        isRefreshingRef.current = true;
        logger.info("Refreshing token proactively");
        authDebug("session.proactive-refresh.start", {
          expiresInMs: tokens.expiresAt - Date.now(),
        });

        // Use HTTP refresh so the backend can rotate the httpOnly refresh cookie.
        // WebSocket refresh can update memory tokens but cannot update cookies,
        // which makes page reload recovery depend on a stale cookie.
        await refreshToken();
        onSessionRefreshedRef.current?.();
        authDebug("session.proactive-refresh.success");

        logger.info("Token refreshed successfully");
      } catch (error) {
        authDebugWarn(
          "session.proactive-refresh.failed",
          describeAuthError(error),
        );
        logRefreshFailure("Failed to refresh token", error);
        onSessionErrorRef.current?.(
          error instanceof Error ? error : new Error("Token refresh failed"),
        );

        openReAuthModal('refresh_failed', user?.email || undefined);
        onSessionExpiredRef.current?.();
      } finally {
        isRefreshingRef.current = false;
      }
    }
  }, [
    isAuthenticated,
    tokens,
    user?.email,
    autoRefresh,
    isTokenExpired,
    isTokenNearExpiration,
    logRefreshFailure,
    refreshToken,
    openReAuthModal,
  ]);

  /**
   * Schedule next token refresh check
   */
  const scheduleRefreshCheck = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (!isAuthenticated || !tokens || !autoRefresh) {
      return;
    }

    const now = Date.now();
    const expirationTime = tokens.expiresAt;
    const thresholdTime = refreshThreshold * 60 * 1000;
    const timeUntilRefresh = Math.max(0, expirationTime - now - thresholdTime);

    // Schedule the next check
    refreshTimeoutRef.current = setTimeout(
      () => {
        refreshIfNeeded();
      },
      Math.min(timeUntilRefresh, 60000),
    ); // Check at least every minute
  }, [isAuthenticated, tokens, autoRefresh, refreshThreshold, refreshIfNeeded]);

  /**
   * Validate current session.
   *
   * Returns `false` only when the user is genuinely not authenticated
   * (no tokens at all). For expired tokens, attempts cookie-backed refresh
   * before re-auth and returns `true` so callers don't hard-redirect.
   */
  const validateSessionImpl = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticated || !tokens) {
      return false;
    }

    if (isTokenExpired()) {
      logger.warn("Session validation: token expired — attempting refresh");
      await refreshIfNeeded();
      return true;
    }

    // Try to refresh if near expiration
    if (isTokenNearExpiration()) {
      try {
        await refreshIfNeeded();
        return true;
      } catch (error) {
        logRefreshFailure("Session validation failed during refresh", error);
        // Even on refresh failure, re-auth modal is opened by refreshIfNeeded
        return true;
      }
    }

    return true;
  }, [
    isAuthenticated,
    tokens,
    isTokenExpired,
    isTokenNearExpiration,
    logRefreshFailure,
    refreshIfNeeded,
  ]);

  // Stabilize validateSession identity with a ref wrapper
  const validateSessionRef = useRef(validateSessionImpl);
  validateSessionRef.current = validateSessionImpl;
  const validateSession = useCallback(
    (): Promise<boolean> => validateSessionRef.current(),
    [],
  );

  /**
   * Extend session activity
   */
  const extendSessionImpl = useCallback(() => {
    if (isAuthenticated && tokens) {
      scheduleRefreshCheck();
    }
  }, [isAuthenticated, tokens, scheduleRefreshCheck]);

  const extendSessionRef = useRef(extendSessionImpl);
  extendSessionRef.current = extendSessionImpl;
  const extendSession = useCallback(
    () => extendSessionRef.current(),
    [],
  );

  /**
   * Get session status
   */
  const getSessionStatus = useCallback(() => {
    if (!isAuthenticated || !tokens) {
      return {
        isValid: false,
        isNearExpiration: false,
        isExpired: true,
        timeUntilExpiration: 0,
      };
    }

    const now = Date.now();
    const timeUntilExpiration = Math.max(0, tokens.expiresAt - now);

    return {
      isValid: true,
      isNearExpiration: isTokenNearExpiration(),
      isExpired: isTokenExpired(),
      timeUntilExpiration,
    };
  }, [isAuthenticated, tokens, isTokenNearExpiration, isTokenExpired]);

  // Setup automatic token refresh
  useEffect(() => {
    if (isAuthenticated && tokens && autoRefresh) {
      scheduleRefreshCheck();
    }

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [isAuthenticated, tokens, autoRefresh, scheduleRefreshCheck]);

  // Visibility change: use refreshIfNeeded (quieter) instead of validateSession
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isAuthenticated) {
        refreshIfNeeded();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, refreshIfNeeded]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  return {
    // Session state
    isAuthenticated,
    user,
    tokens,

    // Session methods
    validateSession,
    refreshIfNeeded,
    extendSession,
    getSessionStatus,

    // Session status
    ...getSessionStatus(),
  };
};

export default useAuthSession;
